import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ProductCard.css';
import { FaChartLine } from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { orderService } from '../../services/orderService';
import Toast from '../../components/Toast';
import { formatCurrency } from '../../utils/format';

const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const {
    id,
    isHot,
    title,
    days,
    dailyIncome,
    investmentQuantity,
    lockedAmount,
    description,
    investmentMin,
    investmentMax,
  } = product;

  const { user, loadUserData } = useUser();
  const [loading, setLoading] = useState(false);

  const handleBuy = async () => {
    if (!user || !user.walletAddress) {
      Toast.warning(t('errors.walletNotConnected'));
      return;
    }

    // 获取用户USDT余额
    // user对象中的usdtBalance可能是BigDecimal类型，需要转换为数字
    const userUsdtBalance = user.usdtBalance != null ? parseFloat(user.usdtBalance) : 0;
    
    // 获取投资金额（使用最小值或让用户输入）
    const amountStr = prompt(`${t('product.enterAmount')} (${investmentMin || 500} - ${investmentMax || 99999})\n${t('product.currentBalance')}: ${formatCurrency(userUsdtBalance)}:`, investmentMin || 500);
    
    if (!amountStr) {
      return; // 用户取消
    }
    
    const amount = parseFloat(amountStr);
    
    if (isNaN(amount) || amount <= 0) {
      Toast.error(t('product.invalidAmount'));
      return;
    }

    if (investmentMin && amount < investmentMin) {
      Toast.error(t('product.amountTooLow', { min: formatCurrency(investmentMin) }));
      return;
    }

    if (investmentMax && amount > investmentMax) {
      Toast.error(t('product.amountTooHigh', { max: formatCurrency(investmentMax) }));
      return;
    }

    // 前端验证：检查USDT余额
    if (userUsdtBalance < amount) {
      Toast.error(t('errors.insufficientBalance'));
      return;
    }

    try {
      setLoading(true);
      const result = await orderService.createOrder(
        user.walletAddress,
        id,
        amount
      );
      Toast.success(t('product.buySuccess'));
      
      // 刷新用户数据以更新余额
      if (loadUserData) {
        await loadUserData(user.walletAddress);
      }
      
      // 触发父组件刷新产品列表（如果有回调）
      // 这里可以添加回调函数来刷新产品列表，而不是reload整个页面
    } catch (error) {
      const errorMsg = error.message || t('errors.unknownError');
      Toast.error(t('product.buyFailed') + ': ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-card">
      {isHot && (
        <div className="hot-badge">
          <span>HOT</span>
        </div>
      )}
      <div className="product-content">
        <div className="product-top-section">
          <div className="product-header-row">
            <div className="product-icon-wrapper">
              <div className="product-icon">
                <FaChartLine className="icon-chart" />
              </div>
            </div>
            <h3 className="product-title">{title}</h3>
          </div>
          <div className="product-info-cards-row">
            <div className="product-stats-card">
              <div className="stat-item">
                <span className="stat-label">{t('product.days')}</span>
                <span className="stat-value pink">{days}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('product.dailyIncome')}</span>
                <span className="stat-value pink">{dailyIncome}</span>
              </div>
            </div>
            <div className="investment-info-card">
              <div className="investment-item">
                <span className="investment-label">{t('product.investmentQuantity')}</span>
                <span className="investment-value green">{investmentQuantity}</span>
              </div>
              <div className="investment-item">
                <span className="investment-label">{t('product.lockedAmount')}</span>
                <span className="investment-value green">{lockedAmount}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="product-bottom-section">
          <p className="product-description">{description}</p>
          <div className="product-action-wrapper">
            <button 
              className="buy-button" 
              onClick={handleBuy}
              disabled={loading}
            >
              {loading ? t('common.loading') : t('product.buy')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;

